// src/services/signatureService.js

/**
 * Centralized signature caching service
 * Provides a way to reduce signature requests by caching and reusing signatures
 */
import * as api from './api';

// Store the last valid signature data
const signatureCache = {
  message: null,
  signature: null,
  timestamp: 0,
  walletAddress: null,
  networkId: null,
  walletType: null
};

/**
 * Helper function to convert network name to network ID
 * @param {string|number} networkIdentifier - Network name or ID
 * @returns {number} - Network ID as a number
 */
const getNetworkId = (networkIdentifier) => {
  // If it's already a number, return it
  if (!isNaN(parseInt(networkIdentifier))) {
    return parseInt(networkIdentifier);
  }
  
  // Map network names to IDs
  const networkMap = {
    'ethereum': 1,
    'bsc': 2,
    'solana': 3,
    'polygon': 4
  };
  
  // Convert to lowercase to ensure matching
  const networkName = String(networkIdentifier).toLowerCase();
  
  // Return mapped ID or default to 1 (ethereum)
  return networkMap[networkName] || 1;
};

/**
 * Get a valid signature with cache management
 * @param {string} walletAddress - User's wallet address
 * @param {string|number} blockchainNetworkId - Blockchain network ID or name
 * @param {string} walletType - Type of wallet
 * @param {boolean} forceNew - Force a new signature even if cached one is valid
 * @returns {Promise<Object>} Response with signature data
 */
export async function getValidSignature(walletAddress, blockchainNetworkId, walletType, forceNew = false) {
  if (!walletAddress || !walletType) {
    throw new Error('Wallet address and type are required');
  }
  
  const networkId = getNetworkId(blockchainNetworkId);
  const currentTime = Date.now();
  
  // Check if we have a valid cached signature that matches the current request
  if (!forceNew && 
      signatureCache.message && 
      signatureCache.signature && 
      signatureCache.walletAddress === walletAddress &&
      signatureCache.networkId === networkId &&
      signatureCache.walletType === walletType &&
      (currentTime - signatureCache.timestamp) < 60000) { // Valid for 60 seconds
    
    // Return cached signature data
    console.log('Using cached signature from', new Date(signatureCache.timestamp).toLocaleTimeString());
    return {
      signature: signatureCache.signature,
      message: signatureCache.message,
      cached: true
    };
  }
  
  // We need a new signature
  console.log('Requesting new signature at', new Date().toLocaleTimeString());
  
  try {
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      networkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    if (!signature) {
      throw new Error('Failed to sign message');
    }
    
    // Update cache
    signatureCache.message = messageResponse.message;
    signatureCache.signature = signature;
    signatureCache.timestamp = currentTime;
    signatureCache.walletAddress = walletAddress;
    signatureCache.networkId = networkId;
    signatureCache.walletType = walletType;
    
    return {
      signature,
      message: messageResponse.message,
      cached: false
    };
  } catch (error) {
    console.error('Error getting signature:', error);
    throw error;
  }
}

/**
 * Clear the signature cache
 * Useful when switching accounts or when a signature is rejected
 */
export function clearSignatureCache() {
  signatureCache.message = null;
  signatureCache.signature = null;
  signatureCache.timestamp = 0;
  signatureCache.walletAddress = null;
  signatureCache.networkId = null;
  signatureCache.walletType = null;
  
  console.log('Signature cache cleared');
}

/**
 * Check if a signature is still valid
 * @param {string} walletAddress - User's wallet address
 * @param {string|number} blockchainNetworkId - Blockchain network ID or name
 * @param {string} walletType - Type of wallet
 * @returns {boolean} - Whether the cached signature is still valid
 */
export function isSignatureValid(walletAddress, blockchainNetworkId, walletType) {
  if (!walletAddress || !walletType) {
    return false;
  }
  
  const networkId = getNetworkId(blockchainNetworkId);
  const currentTime = Date.now();
  
  return (
    signatureCache.message && 
    signatureCache.signature && 
    signatureCache.walletAddress === walletAddress &&
    signatureCache.networkId === networkId &&
    signatureCache.walletType === walletType &&
    (currentTime - signatureCache.timestamp) < 60000 // Valid for 60 seconds
  );
}

export default {
  getValidSignature,
  clearSignatureCache,
  isSignatureValid
};
